import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { CalendarDays, Loader2, MapPin, AlertCircle } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { feriadosWidget } from '@/lib/widgets';
import { FeriadoCard, type Feriado, type DiaPrevisao } from './feriados/FeriadoCard';

export interface FeriadosWidgetProps {
  latitude?: number;
  longitude?: number;
  locationName?: string;
  forecastDays?: number;
  limit?: number;
  year?: number;
}

interface FeriadoRaw {
  data: string;
  nome: string;
  tipo: 'NACIONAL' | 'ESTADUAL' | 'MUNICIPAL' | 'FACULTATIVO';
  uf?: string;
  codigo_ibge?: number | null;
}

function parseDateBR(s: string) {
  const [dd, mm, yyyy] = s.split('/');
  return `${yyyy}-${mm}-${dd}`;
}

function mapTipo(t: FeriadoRaw['tipo']): Feriado['type'] {
  if (t === 'NACIONAL') return 'national';
  if (t === 'ESTADUAL') return 'state';
  if (t === 'MUNICIPAL') return 'municipal';
  return 'optional';
}

const DEFAULT_LAT = -25.55;
const DEFAULT_LON = -48.35;

export function FeriadosWidget({
  latitude = DEFAULT_LAT,
  longitude = DEFAULT_LON,
  locationName = 'Ilha do Mel',
  forecastDays = 16,
  limit = 8,
  year,
}: FeriadosWidgetProps) {
  const { t } = useTranslation();
  const [feriados, setFeriados] = useState<Feriado[]>([]);
  const [previsao, setPrevisao] = useState<Record<string, DiaPrevisao>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const targetYear = year ?? new Date().getFullYear();

    async function fetchData() {
      try {
        const [nRes, eRes, mRes] = await Promise.all([
          fetch('/data/feriados/nacional.json'),
          fetch('/data/feriados/estadual.json'),
          fetch('/data/feriados/paranagua.json'),
        ]);
        if (!nRes.ok || !eRes.ok || !mRes.ok) throw new Error('JSON load failed');

        const [nacional, estadual, municipal]: [FeriadoRaw[], FeriadoRaw[], FeriadoRaw[]] =
          await Promise.all([nRes.json(), eRes.json(), mRes.json()]);

        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);

        const todos: Feriado[] = [...nacional, ...estadual.filter(f => f.uf === 'PR'), ...municipal]
          .map(f => ({ date: parseDateBR(f.data), name: f.nome, type: mapTipo(f.tipo) }))
          .filter(f => {
            const d = new Date(f.date + 'T12:00:00');
            return d.getFullYear() === targetYear && d >= hoje;
          })
          .sort((a, b) => a.date.localeCompare(b.date))
          .slice(0, limit);

        if (cancelled) return;
        setFeriados(todos);

        const weatherRes = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}` +
            `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max` +
            `&timezone=America%2FSao_Paulo&forecast_days=${forecastDays}`
        );
        if (!weatherRes.ok) throw new Error('Weather failed');
        const weather = await weatherRes.json();

        const mapa: Record<string, DiaPrevisao> = {};
        weather.daily.time.forEach((date: string, i: number) => {
          mapa[date] = {
            max: Math.round(weather.daily.temperature_2m_max[i]),
            min: Math.round(weather.daily.temperature_2m_min[i]),
            chuva: weather.daily.precipitation_probability_max[i],
            code: weather.daily.weather_code[i],
          };
        });

        if (!cancelled) setPrevisao(mapa);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [latitude, longitude, forecastDays, limit, year]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-sand-200 shadow-[0_4px_16px_rgba(0,0,0,0.06)] p-6 flex items-center gap-3 text-warm-gray">
        <Loader2 className="w-5 h-5 animate-spin text-teal-deep" />
        <span className="font-body text-sm">{t('widget.feriados.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl border border-sand-200 shadow-[0_4px_16px_rgba(0,0,0,0.06)] p-6 font-body text-sm flex items-center gap-2 text-warm-gray">
        <AlertCircle className="w-5 h-5 text-red-500" />
        {t('widget.feriados.error')}
      </div>
    );
  }

  if (feriados.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-sand-200 shadow-[0_4px_16px_rgba(0,0,0,0.06)] p-6 font-body text-sm text-warm-gray">
        {t('widget.feriados.empty')}
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-2xl border border-sand-200 shadow-[0_4px_16px_rgba(0,0,0,0.06)] overflow-hidden"
      {...feriadosWidget.getDataAttrs()}
    >
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: feriados.map((f, idx) => ({
              '@type': 'ListItem',
              position: idx + 1,
              item: {
                '@type': 'Event',
                name: f.name,
                startDate: f.date,
                location: { '@type': 'Place', name: locationName },
              },
            })),
          })}
        </script>
      </Helmet>

      <div className="bg-gradient-to-r from-teal-deep to-ocean px-6 py-4 text-white flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <CalendarDays className="w-5 h-5" />
          </div>
          <div>
            <h4 className="font-display text-base tracking-wide">{t('widget.feriados.title')}</h4>
            <p className="font-body text-[11px] text-teal-100 flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {locationName}
            </p>
          </div>
        </div>
        <span className="font-body text-[10px] text-teal-100 opacity-80">
          {t('widget.feriados.source')}
        </span>
      </div>

      <div className="p-4 flex flex-col gap-3">
        {feriados.map(f => (
          <FeriadoCard key={f.date} feriado={f} previsao={previsao[f.date]} />
        ))}
      </div>
    </div>
  );
}
